import { supabase } from '@/integrations/supabase/client';
import type { ClubFeatureAccess } from '@/types/feature-access';

/**
 * Service for fetching feature access permissions for clubs
 * Used by CRM to determine which features should be visible/accessible
 */
class FeatureAccessService {
  /**
   * Get all feature access settings for a specific club
   * Returns a map of feature_key -> is_enabled
   */
  async getClubFeatureAccess(clubId: string): Promise<Record<string, boolean>> {
    try {
      const { data, error } = await supabase
        .from('club_feature_access')
        .select('feature_key, is_enabled')
        .eq('club_id', clubId);

      if (error) {
        console.error('Error fetching club feature access:', error);
        // Return empty map on error - all features disabled by default
        return {};
      }

      // Convert array to map: { feature_key: is_enabled }
      const accessMap = (data || []).reduce((acc, row) => {
        acc[row.feature_key] = row.is_enabled;
        return acc;
      }, {} as Record<string, boolean>);

      return accessMap;
    } catch (error) {
      console.error('Unexpected error in getClubFeatureAccess:', error);
      return {};
    }
  }

  /**
   * Check if a specific feature is enabled for a club
   */
  async isFeatureEnabled(clubId: string, featureKey: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('club_feature_access')
        .select('is_enabled')
        .eq('club_id', clubId)
        .eq('feature_key', featureKey)
        .single();

      if (error) {
        // If no record exists, default to false (feature disabled)
        return false;
      }

      return data?.is_enabled ?? false;
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false;
    }
  }
}

export const featureAccessService = new FeatureAccessService();
